import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { JobCard } from '../components/JobCard';
import { toast } from 'sonner';
import { Briefcase, DollarSign, Clock, CheckCircle, Repeat } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // 'active' | 'all' | 'invoiced' | 'recurring'
  const [recurringUnpaid, setRecurringUnpaid] = useState({ visits: [], total_amount: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, statsRes, recurringRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/recurring/unpaid`).catch(() => ({ data: { visits: [], total_amount: 0 } }))
      ]);
      setJobs(jobsRes.data);
      setStats(statsRes.data);
      setRecurringUnpaid(recurringRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      await axios.post(`${API}/jobs/${jobId}/status`, { status: newStatus });
      setJobs(prev => prev.map(job => 
        job.id === jobId ? { ...job, status: newStatus } : job
      ));
      
      // Refresh stats
      const statsRes = await axios.get(`${API}/stats`);
      setStats(statsRes.data);
      
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const filteredJobs = filter === 'active' 
    ? jobs.filter(j => j.status !== 'paid' && !j.is_recurring)
    : filter === 'invoiced'
    ? jobs.filter(j => j.status === 'invoiced')
    : filter === 'recurring'
    ? recurringUnpaid.visits
    : jobs.filter(j => !j.is_recurring); // 'all' - hide parent recurring jobs

  const businessName = user?.business_name || user?.name || 'Your Business';

  return (
    <div className="p-4 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold text-slate-dark">
          {businessName}
        </h1>
        <p className="text-slate-500">Your active jobs</p>
      </div>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-white rounded-xl p-4 border-2 border-slate-200 shadow-hard">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-emerald-600" />
              </div>
              <span className="text-slate-500 text-sm font-medium">Earned</span>
            </div>
            <span className="font-heading text-2xl font-bold text-slate-dark">
              ${stats.total_earned.toLocaleString()}
            </span>
          </div>
          
          <div className="bg-white rounded-xl p-4 border-2 border-slate-200 shadow-hard">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <span className="text-slate-500 text-sm font-medium">Pending</span>
            </div>
            <span className="font-heading text-2xl font-bold text-slate-dark">
              ${stats.total_pending.toLocaleString()}
            </span>
          </div>
        </div>
      )}
      
      {/* Quick Stats Row */}
      {stats && (
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full whitespace-nowrap">
            <Briefcase className="w-4 h-4" />
            <span className="font-bold">{stats.quote_count}</span>
            <span className="text-sm">Quotes</span>
          </div>
          <div className="flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full whitespace-nowrap">
            <Clock className="w-4 h-4" />
            <span className="font-bold">{stats.scheduled_count}</span>
            <span className="text-sm">Scheduled</span>
          </div>
          <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full whitespace-nowrap">
            <CheckCircle className="w-4 h-4" />
            <span className="font-bold">{stats.paid_count}</span>
            <span className="text-sm">Paid</span>
          </div>
          <button 
            onClick={() => navigate('/history')}
            data-testid="view-history"
            className="flex items-center gap-2 bg-slate-100 text-slate-600 px-4 py-2 rounded-full whitespace-nowrap hover:bg-slate-200"
          >
            <span className="text-sm font-medium">View History →</span>
          </button>
        </div>
      )}
      
      {/* Filter Toggle */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          data-testid="filter-active"
          onClick={() => setFilter('active')}
          className={`px-4 py-2 rounded-lg font-bold text-sm transition-colors ${
            filter === 'active'
              ? 'bg-slate-dark text-white'
              : 'bg-white text-slate-600 border-2 border-slate-200'
          }`}
        >
          Active Jobs
        </button>
        <button
          data-testid="filter-invoiced"
          onClick={() => setFilter('invoiced')}
          className={`px-4 py-2 rounded-lg font-bold text-sm transition-colors ${
            filter === 'invoiced'
              ? 'bg-purple-600 text-white'
              : 'bg-white text-slate-600 border-2 border-slate-200'
          }`}
        >
          Awaiting Payment
        </button>
        {recurringUnpaid.visits.length > 0 && (
          <button
            data-testid="filter-recurring"
            onClick={() => setFilter('recurring')}
            className={`px-4 py-2 rounded-lg font-bold text-sm transition-colors flex items-center gap-2 ${
              filter === 'recurring'
                ? 'bg-purple-600 text-white'
                : 'bg-purple-50 text-purple-700 border-2 border-purple-200'
            }`}
          >
            <Repeat className="w-4 h-4" />
            Recurring ({recurringUnpaid.visits.length})
          </button>
        )}
        <button
          data-testid="filter-all"
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-bold text-sm transition-colors ${
            filter === 'all'
              ? 'bg-slate-dark text-white'
              : 'bg-white text-slate-600 border-2 border-slate-200'
          }`}
        >
          All Jobs
        </button>
      </div>
      
      {/* Jobs List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl h-48 animate-pulse" />
          ))}
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="font-heading text-xl font-bold text-slate-dark mb-2">
            No jobs yet
          </h3>
          <p className="text-slate-500">
            Tap the + button to create your first job
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredJobs.map((job, index) => (
            <div 
              key={job.id} 
              className="animate-slide-up"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <JobCard job={job} onStatusChange={handleStatusChange} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
